import type { PayorType } from '../../services/clinicsApi';

export type InsuranceDedupFields = {
  name: string;
  edi_payer_id?: string | null;
};

export type DuplicateInsurancePreview = {
  totalSourceCount: number;
  createdCount: number;
  skippedCount: number;
};

// Medicare payer types (mirrors backend MEDICARE_PAYOR_TYPES in
// app/constants/payor_classification.py). PDGM is NOT gated on this set — the
// flag can be enabled on any payer type; callers use this only to default the
// toggle On when a Medicare type is picked.
export const MEDICARE_PAYOR_TYPES: PayorType[] = [
  'MEDICARE_TRADITIONAL',
  'MEDICARE_ADVANTAGE',
  'MEDICARE_HMO',
];

export const isMedicarePayorType = (pt: PayorType | null): boolean =>
  pt != null && MEDICARE_PAYOR_TYPES.includes(pt);

/** Matches backend `_insurance_dedup_key` in agency_insurance.py. */
export const insuranceDedupKey = (insurance: InsuranceDedupFields): string => {
  const ediPayerId = (insurance.edi_payer_id ?? '').trim();
  const normalizedName = (insurance.name ?? '').trim().toLowerCase();
  if (ediPayerId) {
    return `payer:${ediPayerId}:${normalizedName}`;
  }
  return `name::${normalizedName}`;
};

export const previewDuplicateInsurances = (
  insurances: Array<
    InsuranceDedupFields & { agency_clinic_id?: string | null }
  >,
  sourceClinicId: string,
  destinationClinicId: string
): DuplicateInsurancePreview => {
  const sourceInsurances = insurances.filter(
    insurance => insurance.agency_clinic_id === sourceClinicId
  );
  const existingKeys = new Set(
    insurances
      .filter(insurance => insurance.agency_clinic_id === destinationClinicId)
      .map(insuranceDedupKey)
  );

  let createdCount = 0;
  let skippedCount = 0;
  for (const insurance of sourceInsurances) {
    const key = insuranceDedupKey(insurance);
    if (existingKeys.has(key)) {
      skippedCount += 1;
    } else {
      existingKeys.add(key);
      createdCount += 1;
    }
  }

  return {
    totalSourceCount: sourceInsurances.length,
    createdCount,
    skippedCount,
  };
};

export const formatDuplicateInsurancePreview = (
  preview: DuplicateInsurancePreview
): string => {
  const { totalSourceCount, createdCount, skippedCount } = preview;

  if (totalSourceCount === 0) {
    return 'The source clinic has no insurances to copy.';
  }

  if (createdCount === 0) {
    return `No insurances will be copied — all ${totalSourceCount} source insurance${
      totalSourceCount === 1 ? '' : 's'
    } already exist on the destination (matching payer ID and name).`;
  }

  const createdLabel = `${createdCount} insurance${createdCount === 1 ? '' : 's'}`;
  if (skippedCount === 0) {
    return `Will copy ${createdLabel}.`;
  }

  return `Will copy ${createdLabel} (${skippedCount} skipped — matching payer ID and name already on the destination).`;
};
